"use client"

import * as React from "react"
import { format } from "date-fns"
import Link from "next/link"
import { useTranslations } from "@/components/I18nProvider"
import { GlassBox } from "@/components/custom/box"
import { Button } from "@sarkiai/ui"
import {
  Linkedin,
  Facebook,
  Instagram,
  MapPin,
  Phone,
  Mail,
  Copyright,
} from "lucide-react"
import { CookiePreferencesButton } from "@/components/CookiePreferencesButton"

export function Footer() {
  const t = useTranslations("footer")
  const tApp = useTranslations("appbar")
  const tHero = useTranslations("hero")
  const tFaq = useTranslations("faq")

  const year = format(new Date(), 'yyyy')

  // Organized link groups
  const linkGroups = {
    services: {
      title: tApp('services'),
      links: [
        { label: "VCard", href: "/vcard" },
        { label: "SarkiAI", href: "/sarkiai" },
        { label: tApp('enterprise'), href: "/enterprise" },
      ]
    },
    useCases: {
      title: tApp('use_cases'),
      links: [
        { label: "STD SA", href: "/use-cases/stdsa" },
      ]
    },
    company: {
      title: t('about'),
      links: [
        { label: t('about'), href: "/about" },
        { label: tApp('careers'), href: "/careers" },
        { label: tApp('contact'), href: "/contact" },
        { label: tApp('pricing'), href: "/pricing" },
      ]
    },
    legal: {
      title: t('legal'),
      links: [
        { label: tFaq('title'), href: "/faq" },
        { label: t('privacy'), href: "/privacy" },
        { label: t('cgu'), href: "/cgu" },
      ]
    }
  }

  const socialNetworks = [
    { icon: <Linkedin className="h-5 w-5" />, href: "https://www.linkedin.com/company/djotta", label: t("linkedin") },
    { icon: <Facebook className="h-5 w-5" />, href: "https://web.facebook.com/profile.php?id=61571770161779", label: t("facebook") },
    // { icon: <Instagram className="h-5 w-5" />, href: "https://www.instagram.com/djottalife", label: t("instagram") },
    // { icon: <Youtube className="h-5 w-5" />, href: "", label: t("youtube") }, // Removed as it has no link
  ]

  const companyAddress = [
    { icon: <MapPin className="h-4 w-4" />, label: t('company_location') },
    // { icon: <Phone className="h-4 w-4" />, label: t('contact2'), isPhone: true }, // Commented out as requested
    { icon: <Mail className="h-4 w-4" />, label: t('email'), isEmail: true },
  ]

  return (
    <footer id="footer-section" className="relative pt-16 pb-12 px-4 lg:px-12">
      <GlassBox className="container mx-auto px-4 rounded-[2.5rem] p-8 md:p-12 lg:p-16 border border-foreground/20 relative overflow-hidden group">
        {/* Background Decorative Blur */}
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/20 rounded-full blur-[80px] -z-10 group-hover:bg-primary/30 transition-colors duration-700" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-primary/10 rounded-full blur-[80px] -z-10" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 items-start">
          {/* Brand & Description */}
          <div className="lg:col-span-4 space-y-8">
            <div className="space-y-4 text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl text-foreground tracking-tighter uppercase">
                {tApp('app_name')}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto lg:mx-0">
                {tHero('headline').replace(/\|/g, ' ')}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {companyAddress.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 text-foreground/80 hover:text-foreground transition-colors group/item">
                  <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/5">
                    {item.icon}
                  </div>
                  {item.isEmail ? (
                    <a href={`mailto:${item.label}`} className="text-sm font-semibold tracking-tight">
                      {item.label}
                    </a>
                  ) : (
                    <span className="text-sm font-semibold tracking-tight">{item.label}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Links - Takes remaining space */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
              {/* Services */}
              <div className="space-y-3">
                <h3 className="font-black text-xs uppercase tracking-widest text-foreground">{linkGroups.services.title}</h3>
                <ul className="space-y-2">
                  {linkGroups.services.links.map((link) => (
                    <li key={link.href}>
                      <Link href={link.href} className="text-foreground/70 hover:text-foreground transition-all text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Use Cases */}
              <div className="space-y-3">
                <h3 className="font-black text-xs uppercase tracking-widest text-foreground">{linkGroups.useCases.title}</h3>
                <ul className="space-y-2">
                  {linkGroups.useCases.links.map((link) => (
                    <li key={link.href}>
                      <Link href={link.href} className="text-foreground/70 hover:text-foreground transition-all text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Company */}
              <div className="space-y-3">
                <h3 className="font-black text-xs uppercase tracking-widest text-foreground">{linkGroups.company.title}</h3>
                <ul className="space-y-2">
                  {linkGroups.company.links.map((link) => (
                    <li key={link.href}>
                      <Link href={link.href} className="text-foreground/70 hover:text-foreground transition-all text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Legal */}
              <div className="space-y-3">
                <h3 className="font-black text-xs uppercase tracking-widest text-foreground">{linkGroups.legal.title}</h3>
                <ul className="space-y-2">
                  {linkGroups.legal.links.map((link) => (
                    <li key={link.href}>
                      <Link href={link.href} className="text-foreground/70 hover:text-foreground transition-all text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="pt-2">
                  <CookiePreferencesButton />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social & Copyright - Bottom Center */}
        <div className="flex flex-col items-center gap-6 mt-12 pt-8 border-t border-white/10">
          <div className="flex items-center gap-4">
            {socialNetworks.map((sn, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="icon"
                asChild
                className="h-11 w-11 rounded-xl border-white/10 bg-white/5 text-foreground hover:bg-white/10 hover:text-primary shadow-lg transition-colors"
              >
                <a href={sn.href} target="_blank" rel="noopener noreferrer" aria-label={sn.label}>
                  {sn.icon}
                </a>
              </Button>
            ))}
          </div>

          {/* Copyright */}
          <div className="text-xs font-bold tracking-wider text-muted-foreground/50 text-center">
            <span>{t('trademark', { year: year })}</span>
          </div>
        </div>
      </GlassBox>
    </footer>
  )
}
